import { Dispatch, SetStateAction } from "react";
import type { PromotionalBannerTableRow } from "../../types/api";
import { Box, IconButton, Tooltip } from "@mui/material";
import moment from "moment";
import usePermission from "../../hooks/usePermission";
import Pagination from "../../components/Panigation";
import useTranslation from "../../hooks/traslation";
import PromotionalBannerDetails from "../../Modals/promotionalBannerDetails";
import { SUPPORTED_LANGUAGES } from "../../constants/languages";
import type { PromotionalBannerTexts } from "../../services/promotionalBanners";

type Props = {
  open: boolean;
  openDelete: boolean;
  setOpenDelete: Dispatch<SetStateAction<boolean>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedId: Dispatch<SetStateAction<string>>;
  category: string;
  categoryDisplay?: string;
  texts: PromotionalBannerTexts;
  status: boolean;
  isPrimary: boolean;
  rows: PromotionalBannerTableRow[];
  openDetails: boolean;
  setOpenDetails: Dispatch<SetStateAction<boolean>>;
  setCategory: Dispatch<SetStateAction<string>>;
  setCategoryDisplay?: Dispatch<SetStateAction<string>>;
  setTexts: Dispatch<SetStateAction<PromotionalBannerTexts>>;
  setStatus: Dispatch<SetStateAction<boolean>>;
  setIsPrimary: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  handleEditClick: (row: PromotionalBannerTableRow) => void;
  setRows: Dispatch<SetStateAction<PromotionalBannerTableRow[]>>;
  handlePageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  totalCount: number;
  itemsPerPage: number;
};

const PromotionalBannersList = ({
  setOpenDetails,
  setSelectedId,
  setCategory,
  setCategoryDisplay,
  setTexts,
  setStatus,
  setIsPrimary,
  setOpenDelete,
  rows,
  handleEditClick,
  totalCount,
  page,
  setPage,
  itemsPerPage
}: Props) => {
  const translate = useTranslation();

  const { isAdd, isEdit, isDelete } = usePermission({
    label: "Manage Promotional Banners",
    page_permission: "isView",
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getCategoryDisplay = (row: PromotionalBannerTableRow): string => {
    const c = row?.category;
    if (c && typeof c === "object") return c.name ?? "";
    return typeof c === "string" ? c : "";
  };
  const getCategoryId = (row: PromotionalBannerTableRow) =>
    row?.category && typeof row.category === "object"
      ? row.category._id
      : (row?.category as string) ?? "";

  return (
    <>
      <div className="sorting_table">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>{translate?.promotionalBanner?.category || "Category"}</th>
              {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                <th key={code}>
                  {translate?.promotionalBanner?.text || "Text"} ({label})
                </th>
              ))}
              <th>{translate?.globals?.status || "Status"}</th>
              <th>{translate?.promotionalBanner?.isPrimary || "Primary"}</th>
              <th>{translate?.main?.createdAt || "Created At"}</th>
              <th>{translate?.globals?.actions || "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {rows?.length ? (
              rows?.map((row: PromotionalBannerTableRow) => (
                <tr key={row._id}>
                  <td>{getCategoryDisplay(row)}</td>
                  {SUPPORTED_LANGUAGES.map(({ code }) => (
                    <td
                      key={code}
                      style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {row?.texts?.[code] ?? ""}
                    </td>
                  ))}
                  <td>
                    {row?.status
                      ? translate?.globals?.active || "Active"
                      : translate?.globals?.inactive || "Inactive"}
                  </td>
                  <td>
                    {row?.isPrimary
                      ? translate?.promotionalBanner?.yes || "Yes"
                      : translate?.promotionalBanner?.no || "No"}
                  </td>
                  <td>
                    {row?.createdAt
                      ? moment(row.createdAt).format("DD MMM YYYY")
                      : "-"}
                  </td>
                  <td>
                    <div className="table_actions v2">
                      <Box>
                        <Tooltip title="View">
                          <IconButton
                            onClick={() => {
                              setOpenDetails(true);
                              setSelectedId(row?._id);
                              setCategory(getCategoryId(row));
                              setCategoryDisplay?.(getCategoryDisplay(row) ?? "");
                              setTexts(
                                SUPPORTED_LANGUAGES.reduce<PromotionalBannerTexts>(
                                  (acc, { code }) => {
                                    acc[code] = row?.texts?.[code] ?? "";
                                    return acc;
                                  },
                                  {}
                                )
                              );
                              setStatus(!!row?.status);
                              setIsPrimary(!!row?.isPrimary);
                            }}
                          >
                            <img
                              src="/static/images/eye_icon.svg"
                              alt=""
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {isEdit ? (
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => {
                                handleEditClick(row);
                              }}
                            >
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt=""
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}

                      {isDelete ? (
                        <Box>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => {
                                setSelectedId(row?._id);
                                setOpenDelete(true);
                              }}
                            >
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt=""
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5 + SUPPORTED_LANGUAGES.length}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  {translate?.auth?.noDataFound || "No data found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalCount > 0 ? (
          <Pagination
            setPage={(val) => setPage(Number(val) || 1)}
            module={rows}
            page={page}
            onPageChange={(newPage: number) => {
              setPage(Number(newPage) || 1);
            }}
            totalPages={totalPages}
          />
        ) : null}
      </div>
    </>
  );
};

export default PromotionalBannersList;
